import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, XCircle, Wallet, ArrowRight } from "lucide-react";

export const Route = createFileRoute("/_app/payroll/confirm/$itemId")({
  component: PaystubPage,
});

type Item = {
  id: string;
  user_id: string;
  payroll_run_id: string;
  employee_name: string | null;
  employee_email: string | null;
  hours: number;
  hourly_rate: number;
  pay_type: string;
  gross_pay: number;
  payroll_runs: {
    id: string;
    number: string;
    name: string | null;
    period_start: string;
    period_end: string;
    pay_date: string | null;
    status: string;
    currency: string;
  } | null;
};

function PaystubPage() {
  const { t } = useTranslation();
  const { itemId } = Route.useParams();
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [item, setItem] = useState<Item | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchErr } = await supabase
      .from("payroll_run_items")
      .select(
        "id, user_id, payroll_run_id, employee_name, employee_email, hours, hourly_rate, pay_type, gross_pay, payroll_runs:payroll_run_id(id, number, name, period_start, period_end, pay_date, status, currency)",
      )
      .eq("id", itemId)
      .maybeSingle();

    if (fetchErr) {
      setError(fetchErr.message);
    } else if (!data) {
      setError(
        t("pages.payroll.confirm.notFound", {
          defaultValue:
            "We couldn't find this paystub. It may have been removed or you may not have access.",
        }),
      );
    } else {
      setItem(data as unknown as Item);
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const fmtMoney = (value: number) => {
    const currency = item?.payroll_runs?.currency ?? "USD";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
      }).format(value);
    } catch {
      return value.toFixed(2);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="mx-auto max-w-xl py-10">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-destructive" />
              {t("pages.payroll.confirm.unavailable", {
                defaultValue: "Paystub unavailable",
              })}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {error ??
                t("pages.payroll.confirm.notFound", {
                  defaultValue: "We couldn't find this paystub.",
                })}
            </p>
            <Button asChild variant="outline">
              <Link to="/payroll/my-paystubs">
                {t("pages.payroll.confirm.viewMine", {
                  defaultValue: "Go to my paystubs",
                })}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isOwnItem = profile?.id && item.user_id === profile.id;
  const run = item.payroll_runs;
  const period = run
    ? `${new Date(run.period_start).toLocaleDateString()} – ${new Date(run.period_end).toLocaleDateString()}`
    : "";

  return (
    <div className="mx-auto max-w-xl py-10">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-primary" />
            {t("pages.payroll.confirm.title", { defaultValue: "Your paystub" })}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {run && (
            <div className="rounded-lg bg-muted/40 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {t("pages.payroll.confirm.payrollLabel", { defaultValue: "Payroll" })} {run.number}
              </p>
              <p className="mt-1 text-base font-semibold">{period}</p>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t("pages.payroll.confirm.hours", { defaultValue: "Hours" })}
                  </p>
                  <p className="font-medium tabular-nums">{Number(item.hours).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t("pages.payroll.confirm.rate", { defaultValue: "Rate" })}
                  </p>
                  <p className="font-medium tabular-nums">
                    {fmtMoney(Number(item.hourly_rate))}
                    <span className="ml-1 text-xs text-muted-foreground">
                      {item.pay_type === "daily"
                        ? t("pages.team.payType.dailyShort")
                        : t("pages.team.payType.hourlyShort")}
                    </span>
                  </p>
                </div>

                <div className="col-span-2 border-t border-border pt-3">
                  <p className="text-xs text-muted-foreground">
                    {t("pages.payroll.confirm.gross", { defaultValue: "Gross pay" })}
                  </p>
                  <p className="text-lg font-bold tabular-nums">
                    {fmtMoney(Number(item.gross_pay))}
                  </p>
                </div>
                {run.pay_date && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">
                      {t("pages.payroll.confirm.payDate", { defaultValue: "Pay date" })}
                    </p>
                    <p className="text-sm font-medium">
                      {new Date(run.pay_date).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {!isOwnItem && (
            <div className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
              {t("pages.payroll.confirm.notYours", {
                defaultValue:
                  "This paystub belongs to another employee. Please sign in with the account that received the email.",
              })}
            </div>
          )}

          <Button asChild variant="ghost" className="w-full">
            <Link to="/payroll/my-paystubs">
              {t("pages.payroll.confirm.viewDetails", {
                defaultValue: "View full paystub details",
              })}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
